import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const actionIconStyle = { width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const solidRed = { backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 };
const solidOrange = { backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: '#fff', opacity: 1 };
const solidGreen = { backgroundColor: '#198754', borderColor: '#198754', color: '#fff', opacity: 1 };

function formatDeadline(v) {
  if (!v) return 'No deadline';
  return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function AdminAssignments() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCourses = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setCourses(response.data.courses || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const params = {};
      if (courseFilter) params.courseId = courseFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${apiUrl}/api/admin/assignments`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (response.data.success) setAssignments(response.data.assignments || []);
      else setError(response.data.message || 'Failed to fetch assignments.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching assignments.');
    } finally {
      setLoading(false);
    }
  }, [courseFilter, statusFilter]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const toggleStatus = async (assignment) => {
    const newStatus = assignment.status === 'active' ? 'inactive' : 'active';
    try {
      const token = getToken();
      await axios.put(`${apiUrl}/api/admin/assignments/${assignment.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(`Assignment ${newStatus === 'active' ? 'activated' : 'deactivated'}.`);
      setTimeout(() => setSuccess(''), 3000);
      fetchAssignments();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status.');
    }
  };

  const deleteAssignment = async (assignment) => {
    if (!window.confirm(`Delete "${assignment.title}"? Existing submissions and grades will be kept but this will no longer be visible.`)) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/assignments/${assignment.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) fetchAssignments();
      else setError(response.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting assignment.');
    }
  };

  const filteredAssignments = assignments.filter((a) => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return [a.title, a.coursename, a.batch_names].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Assignments</span></li>
              </ul>
            </div>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => navigate('/admin-assignment-form')}>
              <i className="ph ph-plus me-2" /> Create Assignment
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Search</label>
                  <input
                    type="text"
                    className="form-control py-9"
                    placeholder="Search title, course, or batch..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                  <select className="form-control py-9" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                    <option value="">All Courses</option>
                    {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Status</label>
                  <select className="form-control py-9" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Assignments ({filteredAssignments.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : filteredAssignments.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No assignments found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Course</th>
                        <th>Batches</th>
                        <th>Deadline</th>
                        <th>Submitted</th>
                        <th>Pending Review</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAssignments.map((a) => (
                        <tr key={a.id}>
                          <td className="fw-semibold" style={{ maxWidth: 200, whiteSpace: 'normal' }}>{a.title}</td>
                          <td style={{ maxWidth: 180, whiteSpace: 'normal' }} title={a.coursename}>{a.coursename}</td>
                          <td style={{ maxWidth: 180, whiteSpace: 'normal' }} title={a.batch_names}>{a.batch_names || '—'}</td>
                          <td>{formatDeadline(a.deadline)}</td>
                          <td>{a.submitted_count} / {a.eligible_count}</td>
                          <td>
                            {a.pending_review_count > 0
                              ? <span className="badge bg-warning-subtle text-warning-emphasis">{a.pending_review_count}</span>
                              : <span className="text-gray-400">0</span>}
                          </td>
                          <td>
                            <span className={`badge ${a.status === 'active' ? 'bg-success-subtle text-success-emphasis' : 'bg-neutral-200 text-neutral-600'}`}>{a.status}</span>
                          </td>
                          <td>
                            <div className="d-flex gap-6 flex-wrap">
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Edit" onClick={() => navigate(`/admin-assignment-form/${a.id}`)}>
                                <i className="ph ph-pencil-simple" />
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Submissions" onClick={() => navigate(`/admin-assignment-submissions/${a.id}`)}>
                                <i className="ph ph-tray" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm py-6 px-8"
                                style={{ ...actionIconStyle, ...(a.status === 'active' ? solidOrange : solidGreen) }}
                                title={a.status === 'active' ? 'Deactivate' : 'Activate'}
                                onClick={() => toggleStatus(a)}
                              >
                                <i className={`ph ${a.status === 'active' ? 'ph-pause' : 'ph-play'}`} />
                              </button>
                              <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidRed }} title="Delete" onClick={() => deleteAssignment(a)}>
                                <i className="ph ph-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAssignments;
